import React, { useEffect, useState } from 'react';
import { States } from '@/helpers/common/states';

import useForm from '@/hooks/useForm';
import { unmaskDateAndTime, updateMaskDateAndTime } from '@/helpers/masks/generalDate';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import api from '@/services/api';
import { GatewayPaymentResponse } from '@/model/GatewayPaymentResponse';
import { GatewayPaymentRequest } from '@/model/GatewayPaymentRequest';
import validators from '@/helpers/validators';
import { useDialog } from '@/hooks/useDialog';
import { FormDataGatewayPaymentInputName, ShouldShowModal } from '../types';
import { GatewayPaymentContainer } from './ui';

export const GatewayPaymentScreen: React.FC = (): JSX.Element => {
  const [state, setState] = useState<States>(States.default);
  const [report, setReport] = useState<GatewayPaymentResponse>();

  const { title, visible, onToggle } = useDialog();

  const getStartDate = (): string => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    const dateString = dayjs(date).format(`YYYY-MM-DD HH:mm:ss.SSS`);
    return dateString;
  };

  const getEndDate = (): string => {
    const date = new Date();
    const dateString = dayjs(date).format('YYYY-MM-DD HH:mm:ss.SSS');
    return dateString;
  };

  const { formData, formErrors, onChangeFormInput, isFormValid, resetForm } = useForm({
    initialData: {
      startDate: '',
      endDate: '',
    },
    validators: {
      startDate: [validators.required, validators.validDateAndTime],
      endDate: [validators.required, validators.validDateAndTime],
    },
    formatters: {
      startDate: updateMaskDateAndTime,
      endDate: updateMaskDateAndTime,
    },
  });

  const handleOnGetReport = async (): Promise<void> => {
    try {
      setState(States.loading);
      let startDate = unmaskDateAndTime(formData[FormDataGatewayPaymentInputName.startDate]);
      let endDate = unmaskDateAndTime(formData[FormDataGatewayPaymentInputName.endDate]);
      if (!startDate || !endDate) {
        startDate = getStartDate();
        endDate = getEndDate();
      } else {
        startDate += ':00.000';
        endDate += ':59.999';
      }

      const payload: GatewayPaymentRequest = {
        startDate,
        endDate,
      };
      const { data } = await api.post<GatewayPaymentResponse>('/gateway-payment/report', payload);
      setReport(data);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnClearFilter = (): void => {
    resetForm();
    handleOnGetReport();
  };

  const handleOnFilter = async (): Promise<void> => {
    if (isFormValid()) {
      await handleOnGetReport();
      onToggle();
      resetForm();
    }
  };

  useEffect(() => {
    handleOnGetReport();
  }, []);

  return (
    <GatewayPaymentContainer
      state={state}
      report={report}
      title={title}
      visible={visible}
      onToggle={onToggle}
      shouldShowModal={ShouldShowModal.filter}
      formData={formData}
      formErrors={formErrors}
      onChangeFormInput={onChangeFormInput}
      onClearFilter={handleOnClearFilter}
      onFilter={handleOnFilter}
    />
  );
};
